import React, { useEffect, useMemo, useState } from 'react';
import AdminHeader from '../../components/ui/AdminHeader';
import FacilityInfoSection from './components/FacilityInfoSection';
import OperationalSettingsSection from './components/OperationalSettingsSection';
import BrandingSection from './components/BrandingSection';
import PaymentConfigSection from './components/PaymentConfigSection';
import NotificationTemplatesSection from './components/NotificationTemplatesSection';
import IntegrationSettingsSection from './components/IntegrationSettingsSection';
import VenueCreateModal from './components/VenueCreateModal';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabaseClient';

const VenueSettings = () => {
  const BRANDING_BUCKET = 'venue-branding';

  const emptyFacilityInfo = {
    name: '',
    phone: '',
    address: '',
    parking: '',
    email: '',
    website: '',
    latitude: '',
    longitude: ''
  };

  const emptyOperationalSettings = {
    defaultCourts: '',
    defaultDuration: '',
    availableSports: [],
    minParticipants: '',
    maxParticipants: '',
    enableWaitlist: false,
    
  };

  const emptyBranding = {
    logo: '',
    logoPath: '',
    primaryColor: '',
    secondaryColor: '',
    accentColor: '',
    welcomeMessage: ''
  };

  const emptyPaymentConfig = {
    bankName: '',
    accountNumber: '',
    accountHolder: '',
    paymentDeadline: '',
    autoConfirmPayment: false,
    sendPaymentReminders: false,
    allowPartialRefund: false,
    refundFeePercentage: ''
  };

  const emptyNotificationTemplates = {
    registration:
      '안녕하세요 {name}님,\n\n{session} 세션에 참가 신청이 완료되었습니다.\n\n일시: {date} {time}\n\n결제 안내는 별도로 발송됩니다.\n\n감사합니다.',
    payment:
      '안녕하세요 {name}님,\n\n참가비 {amount} 결제를 안내드립니다.\n\n은행: {bank}\n계좌번호: {account}\n입금 마감: {deadline}\n\n입금 후 확인까지 최대 1시간 소요됩니다.',
    confirmation:
      '안녕하세요 {name}님,\n\n{session} 세션 참가가 확정되었습니다!\n\n일시: {date} {time}\n장소: {location}\n\n세션 당일 뵙겠습니다!',
    reminder:
      '안녕하세요 {name}님,\n\n내일 {session} 세션이 예정되어 있습니다.\n\n일시: {date} {time}\n장소: {location}\n\n준비물을 챙겨주세요!',
    cancellation:
      '안녕하세요 {name}님,\n\n{session} 세션 참가가 취소되었습니다.\n\n사유: {reason}\n\n환불은 영업일 기준 3-5일 소요됩니다.'
  };

  const emptyIntegrationSettings = {
    kakaoEnabled: false,
    kakaoApiKey: '',
    kakaoSenderKey: ''
  };

  const [venues, setVenues] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState('');
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const [facilityInfo, setFacilityInfo] = useState(emptyFacilityInfo);
  const [operationalSettings, setOperationalSettings] = useState(emptyOperationalSettings);
  const [branding, setBranding] = useState(emptyBranding);
  const [paymentConfig, setPaymentConfig] = useState(emptyPaymentConfig);
  const [notificationTemplates, setNotificationTemplates] = useState(emptyNotificationTemplates);
  const [integrationSettings, setIntegrationSettings] = useState(emptyIntegrationSettings);

  const mapVenueRow = (row) => {
    if (!row) return null;
    return {
      id: row.id,
      name: row.name,
      facilityInfo: row.facility_info || {},
      operationalSettings: row.operational_settings || {},
      branding: row.branding || {},
      paymentConfig: row.payment_config || {},
      notificationTemplates: row.notification_templates || {},
      integrationSettings: row.integration_settings || {}
    };
  };

  const loadVenues = async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData?.session?.user?.id) {
        setVenues([]);
        setSelectedVenueId('');
        setLoadError('로그인이 필요합니다.');
        return;
      }

      const { data, error } = await supabase
        .from('venues')
        .select('*')
        .order('created_at', { ascending: true })
        .limit(1);

      if (error) {
        setVenues([]);
        setSelectedVenueId('');
        setLoadError(error?.message || '시설 목록을 불러오지 못했습니다.');
        return;
      }

      const mapped = (data || []).map(mapVenueRow).filter(Boolean);
      setVenues(mapped);
      setSelectedVenueId(mapped?.[0]?.id || '');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadVenues();
  }, []);

  const selectedVenue = useMemo(() => {
    return (venues || [])?.find(v => v?.id === selectedVenueId) || null;
  }, [venues, selectedVenueId]);

  useEffect(() => {
    if (!selectedVenue) return;
    setFacilityInfo({ ...emptyFacilityInfo, ...(selectedVenue?.facilityInfo || {}) });
    setOperationalSettings({
      ...emptyOperationalSettings,
      ...(selectedVenue?.operationalSettings || {})
    });
    setBranding({ ...emptyBranding, ...(selectedVenue?.branding || {}) });
    setPaymentConfig({ ...emptyPaymentConfig, ...(selectedVenue?.paymentConfig || {}) });
    setNotificationTemplates({
      ...emptyNotificationTemplates,
      ...(selectedVenue?.notificationTemplates || {})
    });
    setIntegrationSettings({
      ...emptyIntegrationSettings,
      ...(selectedVenue?.integrationSettings || {})
    });
  }, [selectedVenue]);

  const handleCreateVenue = async (formInput) => {
    if (venues?.length > 0 || selectedVenueId) {
      throw new Error('시설은 1개만 등록할 수 있습니다.');
    }

    const name = String(formInput?.name || '').trim();
    if (!name) throw new Error('시설명을 입력해주세요.');

    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) {
      throw new Error('로그인이 필요합니다.');
    }

    const { data, error } = await supabase
      .from('venues')
      .insert({
        client_id: userId,
        name,
        facility_info: {
          name,
          phone: formInput?.phone || '',
          address: formInput?.address || '',
          parking: formInput?.parking || '',
          email: formInput?.email || '',
          website: formInput?.website || '',
          latitude: formInput?.latitude || '',
          longitude: formInput?.longitude || ''
        },
        operational_settings: {
          defaultCourts: formInput?.defaultCourts || '',
          defaultDuration: formInput?.defaultDuration || '120',
          minParticipants: formInput?.minParticipants || '',
          maxParticipants: formInput?.maxParticipants || '',
          availableSports: formInput?.availableSports || ['pickleball'],
          enableWaitlist: false
        },
        branding: {},
        payment_config: {
          bankName: formInput?.bankName || '',
          accountNumber: formInput?.accountNumber || '',
          accountHolder: formInput?.accountHolder || '',
          paymentDeadline: formInput?.paymentDeadline || '3',
          autoConfirmPayment: false,
          sendPaymentReminders: false,
          allowPartialRefund: false,
          refundFeePercentage: ''
        },
        notification_templates: emptyNotificationTemplates,
        integration_settings: emptyIntegrationSettings
      })
      .select('*')
      .single();

    if (error) {
      throw new Error(error?.message || '시설 추가에 실패했습니다.');
    }

    const created = mapVenueRow(data);
    setVenues(prev => [...(prev || []), created].filter(Boolean));
    setSelectedVenueId(created?.id || '');
  };

  const handleSaveFacilityInfo = async (data) => {
    if (!selectedVenueId) throw new Error('시설을 선택해주세요.');

    const { error } = await supabase
      .from('venues')
      .update({
        name: data?.name || selectedVenue?.name,
        facility_info: data
      })
      .eq('id', selectedVenueId);

    if (error) throw new Error(error?.message || '저장에 실패했습니다.');
    setFacilityInfo(data);
    await loadVenues();
    alert('시설 정보가 저장되었습니다.');
  };

  const handleSaveOperationalSettings = async (data) => {
    if (!selectedVenueId) throw new Error('시설을 선택해주세요.');
    const { error } = await supabase
      .from('venues')
      .update({ operational_settings: data })
      .eq('id', selectedVenueId);
    if (error) throw new Error(error?.message || '저장에 실패했습니다.');
    setOperationalSettings(data);
    alert('운영 설정이 저장되었습니다.');
  };

  const handleSaveBranding = async (data) => {
    if (!selectedVenueId) throw new Error('시설을 선택해주세요.');
    const merged = { ...(branding || {}), ...(data || {}) };
    const { error } = await supabase
      .from('venues')
      .update({ branding: merged })
      .eq('id', selectedVenueId);
    if (error) throw new Error(error?.message || '저장에 실패했습니다.');
    setBranding(merged);
    alert('브랜딩 설정이 저장되었습니다.');
  };

  const handleUploadBrandingLogo = async (file) => {
    if (!selectedVenueId) throw new Error('시설을 선택해주세요.');

    const prevPath = branding?.logoPath;
    if (prevPath) {
      await supabase.storage.from(BRANDING_BUCKET).remove([prevPath]);
    }

    const id = globalThis.crypto?.randomUUID?.() || Math.random().toString(36).slice(2);
    const objectPath = `${selectedVenueId}/${id}.jpg`;

    const { error: uploadError } = await supabase.storage
      .from(BRANDING_BUCKET)
      .upload(objectPath, file, {
        contentType: file?.type || undefined,
        upsert: false
      });
    if (uploadError) throw new Error(uploadError?.message || '로고 업로드에 실패했습니다.');

    const { data: publicData } = supabase.storage.from(BRANDING_BUCKET).getPublicUrl(objectPath);
    const publicUrl = publicData?.publicUrl;
    if (!publicUrl) throw new Error('업로드된 로고 URL을 가져오지 못했습니다.');

    const nextBranding = {
      ...(branding || {}),
      logo: publicUrl,
      logoPath: objectPath
    };
    await handleSaveBranding(nextBranding);
    return { publicUrl, objectPath };
  };

  const handleDeleteBrandingLogo = async () => {
    if (!selectedVenueId) throw new Error('시설을 선택해주세요.');
    const prevPath = branding?.logoPath;
    if (prevPath) {
      const { error: removeError } = await supabase.storage.from(BRANDING_BUCKET).remove([prevPath]);
      if (removeError) throw new Error(removeError?.message || '로고 삭제에 실패했습니다.');
    }

    const nextBranding = {
      ...(branding || {}),
      logo: '',
      logoPath: ''
    };
    await handleSaveBranding(nextBranding);
  };

  const handleSavePaymentConfig = async (data) => {
    if (!selectedVenueId) throw new Error('시설을 선택해주세요.');
    const { error } = await supabase
      .from('venues')
      .update({ payment_config: data })
      .eq('id', selectedVenueId);
    if (error) throw new Error(error?.message || '저장에 실패했습니다.');
    setPaymentConfig(data);
    alert('결제 설정이 저장되었습니다.');
  };

  const handleSaveNotificationTemplates = async (data) => {
    if (!selectedVenueId) throw new Error('시설을 선택해주세요.');
    const { error } = await supabase
      .from('venues')
      .update({ notification_templates: data })
      .eq('id', selectedVenueId);
    if (error) throw new Error(error?.message || '저장에 실패했습니다.');
    setNotificationTemplates(data);
    alert('알림 템플릿이 저장되었습니다.');
  };

  const handleSaveIntegrationSettings = async (data) => {
    if (!selectedVenueId) throw new Error('시설을 선택해주세요.');
    const { error } = await supabase
      .from('venues')
      .update({ integration_settings: data })
      .eq('id', selectedVenueId);
    if (error) throw new Error(error?.message || '저장에 실패했습니다.');
    setIntegrationSettings(data);
    alert('연동 설정이 저장되었습니다.');
  };

  return (
    <div className="min-h-screen bg-background">
      <AdminHeader />
      
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 md:mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-2">
                시설 설정
              </h1>
              <p className="text-base md:text-lg text-muted-foreground">
                시설의 프로필과 운영 정책을 관리합니다
              </p>
            </div>

            {!selectedVenueId && (
              <Button
                variant="default"
                size="lg"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setCreateModalOpen(true)}
                disabled={isLoading}
              >
                시설 등록
              </Button>
            )}
          </div>

          {loadError && (
            <div className="mb-6 md:mb-8 bg-error/10 border border-error/20 rounded-xl p-4">
              <p className="text-sm text-error">{loadError}</p>
            </div>
          )}

          {selectedVenueId ? (
            <div className="space-y-6 md:space-y-8">
              <FacilityInfoSection
                data={facilityInfo}
                onChange={setFacilityInfo}
                onSave={handleSaveFacilityInfo}
              />

              <OperationalSettingsSection
                data={operationalSettings}
                onChange={setOperationalSettings}
                onSave={handleSaveOperationalSettings}
              />

              <BrandingSection
                data={branding}
                onChange={setBranding}
                onSave={handleSaveBranding}
                onUploadLogo={handleUploadBrandingLogo}
                onDeleteLogo={handleDeleteBrandingLogo}
              />

              <PaymentConfigSection
                data={paymentConfig}
                onChange={setPaymentConfig}
                onSave={handleSavePaymentConfig}
              />

              <NotificationTemplatesSection
                data={notificationTemplates}
                onChange={setNotificationTemplates}
                onSave={handleSaveNotificationTemplates}
              />

              <IntegrationSettingsSection
                data={integrationSettings}
                onChange={setIntegrationSettings}
                onSave={handleSaveIntegrationSettings}
              />
            </div>
          ) : (
            <div className="bg-card border border-border rounded-xl p-6 text-center">
              <p className="text-muted-foreground">
                먼저 시설을 추가하거나 선택해주세요.
              </p>
            </div>
          )}
        </div>
      </main>

      <VenueCreateModal
        isOpen={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreate={handleCreateVenue}
      />
    </div>
  );
};

export default VenueSettings;